// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { isRowPresentOnDay } from '@/report/utilities/to-presence-bits';
import {
  GridAggregationFunction,
  GridAggregationModel,
} from '@mui/x-data-grid-premium';
import { sum } from 'lodash-es';

/**
 * Returns the aggregation function name for a day column.
 */
export function toDayAggregationName(dayIndex: number) {
  return `assetCount:${dayIndex.toString()}`;
}

/**
 * Builds one aggregation function per day, each reading the day it closes over.
 */
export function createDayAggregationFunctions(dayCount: number) {
  const functions: Record<string, GridAggregationFunction<number, number>> = {};

  for (let dayIndex = 0; dayIndex < dayCount; dayIndex++) {
    functions[toDayAggregationName(dayIndex)] = {
      // Material UI has no option to hide this label in the column header.
      label: '',
      getCellValue: (parameters) => {
        return isRowPresentOnDay(parameters.row, dayIndex, dayCount) ? 1 : 0;
      },
      apply: (parameters) => {
        return sum(parameters.values);
      },
      // Material UI cannot infer the value type for valueFormatter, so it is explicit.
      valueFormatter: (value: number) => {
        if (typeof value === 'number') {
          return value.toLocaleString();
        }

        return value;
      },
    };
  }

  return functions;
}

/**
 * Maps each date column within the reported window to the aggregation function for its day.
 *
 * Bounded by the same dayCount the functions are built from. Material UI drops a model entry
 * whose function is missing without saying so, and taking dayCount as a dependency is also
 * what makes the grid re-derive its rules once a report arrives.
 */
export function createDayAggregationModel(dates: string[], dayCount: number) {
  return dates.slice(0, dayCount).reduce<GridAggregationModel>(
    (model, date, dayIndex) => ({
      ...model,
      [date]: toDayAggregationName(dayIndex),
    }),
    {}
  );
}
